import React, { useEffect, useMemo, useState } from "react";

// Three handwriting fonts shipped by Google Fonts. Loaded at runtime via a
// stylesheet link so we don't bloat the initial bundle.
const FONTS = [
  { id: "caveat", name: "Caveat", css: "Caveat, cursive" },
  { id: "dancing", name: "Dancing Script", css: "'Dancing Script', cursive" },
  { id: "great-vibes", name: "Great Vibes", css: "'Great Vibes', cursive" }
];

const FONT_STYLESHEET_HREF =
  "https://fonts.googleapis.com/css2?family=Caveat:wght@600&family=Dancing+Script:wght@600&family=Great+Vibes&display=swap";

function ensureFontsLoaded() {
  if (typeof document === "undefined") return;
  if (document.getElementById("docsign-typed-fonts")) return;
  const link = document.createElement("link");
  link.id = "docsign-typed-fonts";
  link.rel = "stylesheet";
  link.href = FONT_STYLESHEET_HREF;
  document.head.appendChild(link);
}

// Render the given text in the given font into an off-screen canvas, trim
// the transparent padding so the signature fills its bounding box, and
// return a PNG data URL. Same output shape as SignaturePad so
// submitSignature stays unchanged.
async function renderToPng(text, fontCss) {
  // Wait for the web font to be ready so the canvas picks up the right glyphs.
  if (document.fonts?.load) {
    try {
      await document.fonts.load(`64px ${fontCss}`);
    } catch {
      /* ignore */
    }
  }
  const canvas = document.createElement("canvas");
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  canvas.width = 900 * ratio;
  canvas.height = 240 * ratio;
  const ctx = canvas.getContext("2d");
  ctx.scale(ratio, ratio);
  ctx.clearRect(0, 0, 900, 240);
  ctx.fillStyle = "#0f172a";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.font = `64px ${fontCss}`;
  ctx.fillText(text, 450, 120);
  return trimTransparent(canvas);
}

function trimTransparent(canvas) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return canvas.toDataURL("image/png");
  const pad = Math.max(8, Math.round(Math.max(width, height) * 0.02));
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(width - 1, maxX + pad);
  maxY = Math.min(height - 1, maxY + pad);
  const w = maxX - minX + 1;
  const h = maxY - minY + 1;
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  out.getContext("2d").drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
  return out.toDataURL("image/png");
}

export default function TypedSignature({
  open,
  defaultName = "",
  onCancel,
  onAccept
}) {
  const [name, setName] = useState(defaultName);
  const [fontId, setFontId] = useState(FONTS[0].id);

  useEffect(() => {
    if (open) {
      ensureFontsLoaded();
      setName(defaultName);
    }
  }, [open, defaultName]);

  const font = useMemo(
    () => FONTS.find((f) => f.id === fontId) ?? FONTS[0],
    [fontId]
  );

  if (!open) return null;

  const accept = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const dataUrl = await renderToPng(trimmed, font.css);
    onAccept(dataUrl);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 p-3">
        <span className="font-semibold">Type your signature</span>
        <button className="btn-secondary text-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="flex-1 space-y-4 p-4">
        <label className="block">
          <span className="text-sm text-slate-700">Your name</span>
          <input
            className="input mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </label>
        <div className="space-y-2">
          <span className="text-sm text-slate-700">Style</span>
          {FONTS.map((f) => (
            <label
              key={f.id}
              className={
                "flex cursor-pointer items-center justify-between rounded-lg border px-3 py-3 " +
                (fontId === f.id
                  ? "border-brand-500 bg-brand-50"
                  : "border-slate-300 bg-white")
              }
            >
              <input
                type="radio"
                className="sr-only"
                checked={fontId === f.id}
                onChange={() => setFontId(f.id)}
              />
              <span
                className="truncate text-3xl leading-none"
                style={{ fontFamily: f.css }}
              >
                {name.trim() || "Your name"}
              </span>
              <span className="ml-3 text-xs text-slate-500">{f.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-slate-200 p-3">
        <button
          className="btn-primary"
          onClick={accept}
          disabled={!name.trim()}
        >
          Accept signature
        </button>
      </div>
    </div>
  );
}
